import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookPrint } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import partial from "lodash-es/partial"
import type { Narrative } from "@shared/actions/create-narrative"
import createNarrative from "@shared/actions/create-narrative"

export interface Payload {
  planbookId: string
  currentUserId: string
  settingsToSave: Record<string, any>
  perspective: string
  contextForAnalytics: Record<string, any>
}

export default function prepareSetUserLastPrintOptions(payload: Payload): PreparedAction {
  return {
    name: "SET_USERS_LAST_PRINT_OPTIONS",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("user", payload.currentUserId).set(
    `attributes.printOptions.${payload.perspective}`,
    payload.settingsToSave
  ).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let primaryObjectSpecifier
  switch (payload.perspective) {
    case "course-date:single":
    case "course-date:range":
      primaryObjectSpecifier = "lesson"
      break
    case "planbook-date:single":
      primaryObjectSpecifier = "day"
      break
    case "planbook-date:range:columns":
    case "planbook-date:range:full-page":
      primaryObjectSpecifier = "week"
      break
    case "planbook-month:grid":
      primaryObjectSpecifier = "month"
      break
    case "course-outline":
      primaryObjectSpecifier = "outline"
      break
    case "course-unit":
      primaryObjectSpecifier = "unit"
      break
    case "course-units":
      primaryObjectSpecifier = "timeline"
      break
    case "print.planbook-standards":
      primaryObjectSpecifier = "standards_tracker"
      break
    default:
      primaryObjectSpecifier = null
  }
  let newEvent: PlanbookPrint = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    primary_object_type: "planbook",
    primary_possessive: "owns",
    primary_object_id_and_ancestor_ids: [payload.planbookId],
    primary_verb: "print",
    primary_object_specifier: primaryObjectSpecifier,
    adverbial_verb: null,
    adverbial_object_type: null,
    adverbial_object_specifier: null,
  }
  return createNarrative({
    title: `Print`,
    titleForAnalytics: "Print",
    description: `Printing the ${primaryObjectSpecifier}`,
    context: payload.contextForAnalytics,
    newAnalyticsEvents: [newEvent],
  })
}
